import React, { useEffect, useMemo, useRef, useState } from 'react';

import * as styles from './index.module.scss';
import SecondaryLayout from '../../layout/secondaryLayout';
import classNames from 'classnames';
import ArrowRightIcon from '../../components/icons/arrowRight1';
import { CheckCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Button, ConfigProvider, Form, Input, InputNumber, Spin } from 'antd';
import { useScroll } from 'ahooks';
import { useNodeDetail } from '@/hooks/useNodeDetail';
import LINKS from '@/constants';
import { subscribeFloatingNews } from '@/components/floating-news';

const FormItem = Form.Item;
const Index = () => {
  const [form] = Form.useForm();
  const address = Form.useWatch('address', form);
  const [step, setStep] = useState(0);
  const [isSuccess, setIsSuccess] = useState(true);
  const [isScrolling, setIsScrolling] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState(null); // 当前活跃的菜单项

  const [hasFloatingNews, setHasFloatingNews] = useState(false);
  const [floatingNewsHeight, setFloatingNewsHeight] = useState(0);
  const [shouldAnimate, setShouldAnimate] = useState(false);

  const onGetdataError = (error) => {
    setIsSuccess(false);
  };
  const { getNodeDetail, nodeDetail, nodeDetailLoading } =
    useNodeDetail(onGetdataError);

  const noticeRef = useRef<HTMLDivElement | null>(null);
  const hardwareRef = useRef<HTMLDivElement | null>(null);
  const downloadRef = useRef<HTMLDivElement | null>(null);
  const infoRef = useRef<HTMLDivElement | null>(null);
  const confirmRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const sections = [
    { id: 'notice', label: 'Notice', ref: noticeRef },
    { id: 'hardware', label: 'Hardware Requirements', ref: hardwareRef },
    { id: 'download', label: 'Download', ref: downloadRef },
    { id: 'infos', label: 'Information Review', ref: infoRef },
    { id: 'confirm', label: 'Confirm', ref: confirmRef },
  ];

  // 点击菜单项，滚动到对应的 section
  const scrollToSection = (index: number) => {
    const section = sections[index];
    if (section && section.ref.current) {
      // 标记为正在滚动
      setIsScrolling(true);

      // 平滑滚动到指定区域
      section.ref.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });

      // 滚动完成后，重置滚动状态
      setTimeout(() => {
        setIsScrolling(false); // 滚动结束后，恢复滚动状态
      }, 1000); // 设置延迟，确保滚动完成后再进行更新
    }
  };

  // IntersectionObserver 用于设置菜单项的激活状态
  useEffect(() => {
    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        // 如果正在滚动，则跳过
        if (isScrolling) return;

        let firstIntersectingIndex = -1;

        // 遍历所有 entry，找出第一个进入视口的 section
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = sections.findIndex(
              (section) => section.ref.current === entry.target
            );

            if (index !== -1 && firstIntersectingIndex === -1) {
              firstIntersectingIndex = index; // 获取第一个完全进入视口的 section
            }
          }
        });

        // 如果找到了进入视口的第一个 section，则更新 active 状态
        if (
          firstIntersectingIndex !== -1 &&
          firstIntersectingIndex !== activeIndex
        ) {
          setStep(firstIntersectingIndex); // 更新活跃菜单项
          setActiveIndex(firstIntersectingIndex); // 更新当前激活的菜单项索引
        }
      },
      {
        root: null, // 使用视口作为 root
        rootMargin: '0px', // 视口外延
        threshold: 0.5, // 至少 50% 可见时才触发
      }
    );

    // 观察每个 section 元素
    sections.forEach((section) => {
      if (section.ref.current) {
        intersectionObserver.observe(section.ref.current);
      }
    });

    // 清除观察器
    return () => {
      intersectionObserver.disconnect();
    };
  }, [sections, isScrolling, activeIndex]); // 添加 isScrolling 和 activeIndex 作为依赖

  const queryAddress = async () => {
    resetFields();

    const addr = form.getFieldValue('address');

    try {
      await getNodeDetail(addr);
    } catch (error) {}
  };

  const resetFields = () => {
    const fields = [
      'moniker',
      'website',
      'details',
      'rate',
      'maxRate',
      'maxChangeRate',
      'tokens',
    ];
    form.resetFields(fields);
    setIsSuccess(true);
  };

  useEffect(() => {
    if (nodeDetail) {
      const { data } = nodeDetail;
      const { detail } = data;
      form.setFieldValue('moniker', detail?.description?.moniker);
      form.setFieldValue('website', detail?.description?.website || '-');
      form.setFieldValue('details', detail?.description?.details || '-');
      form.setFieldValue(
        'rate',
        Number(detail?.commission?.commission_rates?.rate).toFixed(3) || '-'
      );
      form.setFieldValue(
        'maxRate',
        Number(detail?.commission?.commission_rates?.max_rate).toFixed(3) || '-'
      );
      form.setFieldValue(
        'maxChangeRate',
        Number(detail?.commission?.commission_rates?.max_change_rate).toFixed(
          3
        ) || '-'
      );
      form.setFieldValue(
        'tokens',
        Number(detail?.tokens) / Math.pow(10, 18) || '-'
      );
    }
  }, [nodeDetail]);

  // Subscribe to floating news visibility changes
  useEffect(() => {
    const unsubscribe = subscribeFloatingNews((isVisible, height, animate) => {
      setFloatingNewsHeight(height);
      setHasFloatingNews(isVisible);
      setShouldAnimate(animate);
    });
    return () => unsubscribe();
  }, []);

  const floatingNewsStyle = useMemo(() => {
    const style = {
      paddingTop: hasFloatingNews ? `${floatingNewsHeight}px` : 0,
      transition: shouldAnimate ? 'padding-top 0.3s ease' : 'none',
    };
    return style;
  }, [hasFloatingNews, floatingNewsHeight, shouldAnimate]);

  return (
    <SecondaryLayout type="node">
      <ConfigProvider
        theme={{
          token: {
            colorBgContainer: '#1F294E',
            colorBorder: 'rgba(49, 106, 255, 0.40)',
            colorError: 'rgba(182, 72, 71, 0.50)',
            colorErrorBg: 'rgba(146, 49, 49, 0.20)',
            colorErrorText: '#B64847',
            colorErrorBorder: '#923131',
            colorErrorBorderHover: '#923131',
            borderRadius: 12,
            colorTextPlaceholder: 'rgba(167, 192, 255, 0.40)',
            colorText: '#A7C0FF',
            boxShadow: 'none',
          },
          components: {
            Form: {
              verticalLabelPadding: '8px 0',
              itemMarginBottom: 12,
              labelFontSize: 14,
              labelColor: 'rgba(167, 192, 255, 0.80)',
            },
            Input: {
              colorErrorBg: 'rgba(146, 49, 49, 0.20)',

              activeBorderColor: '#316AFF',
              activeShadow: 'none',
              activeBg: '#283976',
              hoverBorderColor: '#316AFF',
              inputFontSize: 18,
              paddingBlock: 10,
              paddingInline: 20,
              errorActiveShadow: 'none',
            },
            InputNumber: {
              activeBorderColor: '#316AFF',
              activeShadow: 'none',
              activeBg: '#283976',
              hoverBorderColor: '#316AFF',
              inputFontSize: 18,
              paddingBlock: 10,
              paddingInline: 20,
              errorActiveShadow: 'none',
            },
          },
        }}
      >
        <Spin fullscreen spinning={nodeDetailLoading}></Spin>
        <Form
          layout="vertical"
          form={form}
          scrollToFirstError
          requiredMark={false}
        >
          <div className={styles.content} ref={contentRef}>
            <div className={styles.runNode}>
              <div style={floatingNewsStyle}></div>
              {/* 左侧fix导航 */}
              <div className={styles.left}>
                <div style={floatingNewsStyle}></div>
                {sections.map((item, idx) => {
                  return (
                    <div
                      className={classNames(
                        styles.item,
                        idx === step && styles.active
                      )}
                      onClick={() => {
                        setStep(idx);
                        scrollToSection(idx);
                      }}
                    >
                      Step {idx + 1} <span>{item.label}</span>
                    </div>
                  );
                })}
              </div>
              {/* 右侧内容 */}
              <div className={styles.rightContent}>
                {/* Notice */}
                <div className={styles.card} id="notice" ref={noticeRef}>
                  <div className={styles.title}>
                    <img
                      src="https://static-s3.mtt.xyz/chain-website/902544e9fb80ff614eeea397463be9f3.png"
                      alt=""
                    />
                    Notice
                  </div>
                  <div className={styles.cardContent}>
                    Becoming a validator node for the MTT Network is a critical
                    responsibility for promoting decentralized network
                    development and ensuring its security. Before applying,
                    please carefully review the following points:
                    <ul>
                      <li>
                        <div>Hardware and Network Configuration</div>
                        MTT Network nodes require stable hardware and high-speed
                        network connections. It is recommended to use
                        high-performance servers with sufficient CPU, memory,
                        and SSD storage to handle validation tasks and on-chain
                        data storage needs. The network should ensure low
                        latency and high bandwidth to avoid delays or offline
                        status caused by instability. During operations,
                        consider backup servers and redundant network
                        configurations to minimize the risk of system crashes or
                        service interruptions. Continuous monitoring of hardware
                        and network performance will enhance overall node
                        efficiency and ensure smooth operations.
                      </li>
                      <li>
                        <div>Staking Requirements and Slashing Mechanisms</div>
                        Validators must stake a certain amount of MTT tokens to
                        qualify, serving as both a commitment to the network and
                        a security measure. Staked tokens are locked via smart
                        contracts and cannot be withdrawn during the active
                        period. Poor performance, such as frequent offline
                        status or double signing, will result in slashing
                        penalties, reducing a portion of the staked assets. To
                        mitigate risks, operators should ensure high
                        availability of equipment and promptly resolve issues
                        that may affect node performance. The staking mechanism
                        also reflects a node's competitiveness and credibility,
                        encouraging validators to maintain optimal performance.
                      </li>
                      <li>
                        <div>
                          Validator Responsibilities and Reward Mechanism
                        </div>
                        Validators are responsible for validating transactions,
                        proposing blocks, and maintaining the integrity of
                        on-chain data. By fulfilling these duties, nodes earn
                        block rewards and gas fees, which are automatically
                        distributed at fixed intervals. Operators need to
                        distribute rewards transparently and promptly based on
                        internal staking ratios. High-performance nodes are more
                        likely to gain block production privileges, thus earning
                        higher rewards. To ensure long-term profitability, nodes
                        should operate reliably and regularly optimize hardware
                        and software performance.
                      </li>
                      <li>
                        <div>Operations and Security Assurance</div>
                        High availability is a core requirement for validators,
                        as nodes must maintain nearly 100% uptime to avoid
                        missing validation tasks or being removed from the
                        active validator set. Operators should adopt reliable
                        monitoring tools to track operational status in real
                        time and promptly address potential issues.
                        Additionally, encrypted communication, firewalls, and
                        other measures are recommended to protect nodes from
                        DDoS or other network attacks. Regularly updating node
                        software and applying security patches are essential for
                        maintaining compatibility and data security.
                      </li>
                      <li>
                        <div>Delegation and Reward Distribution</div>
                        Even if you are not running a node directly, you can
                        participate in mining and earn rewards by delegating
                        your tokens to a validator. Delegators pay a commission
                        fee, the rate of which is set by the node operator.
                        Rewards for delegators are automatically distributed
                        based on the node's block production performance and
                        staking ratio. Operators must maintain transparent
                        communication with delegators, providing regular updates
                        on node status to attract more stakers. Delegators
                        should also be aware of potential lock-up periods for
                        reward distribution.
                      </li>
                      <li>
                        <div>Node Application and Compliance Requirements</div>
                        Applying to become a validator requires following the
                        guidelines outlined in the official documentation,
                        including downloading the client, configuring hardware,
                        and staking tokens. After submission, the network will
                        review the node's setup and qualifications; only those
                        meeting the criteria will be approved to join the active
                        validator pool. During operations, validators must
                        strictly adhere to MTT Network's protocols and conduct
                        rules, avoiding any unauthorized actions or malicious
                        behavior. Violations may result in permanent
                        disqualification as a validator and potential loss of
                        staked assets.
                      </li>
                    </ul>
                    <div>
                      For more detailed information, click “Learn More” to
                      explore the MTT Validator Protocol or contact us at{' '}
                      <a href="mailto:support@mtt.network">
                        support@mtt.network
                      </a>
                    </div>
                  </div>
                  <a
                    href="https://mtt-network.gitbook.io/mtt-validator-protocol"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Learn More <ArrowRightIcon />
                  </a>
                </div>
                {/* Hardware Requirements */}
                <div className={styles.card} id="hardware" ref={hardwareRef}>
                  <div className={styles.title}>
                    <img
                      src="https://static-s3.mtt.xyz/chain-website/16dc807d7a3662048df8b3e8010705a1.png"
                      alt=""
                    />
                    Hardware Requirements
                  </div>
                  <div className={styles.tips}>
                    <InfoCircleOutlined className={styles.icon} />
                    Pleasa note that MTT nodes have high network requirements,
                    so always ensure network stability.
                  </div>
                  {/* <div className={styles.requirementsCard}>
                    <div className={styles.minimum}>Minimum</div>
                    <div className={styles.lines}>
                      <div className={styles.line}>
                        <span>CPUs</span>
                        <i>8-core CPU</i>
                      </div>
                      <div className={styles.line}>
                        <span>RAM</span>
                        <i>16GB</i>
                      </div>
                      <div className={styles.line}>
                        <span>Storage (SSD)</span>
                        <i>1TB SSD Storage</i>
                      </div>
                      <div className={styles.line}>
                        <span>Network</span>
                        <i>100Mbps for uplink and downlink bandwidth</i>
                      </div>
                    </div>
                  </div> */}
                  <div className={styles.requirementsCard}>
                    <div className={styles.higher}>Recommended</div>
                    <div className={styles.lines}>
                      <div className={styles.line}>
                        <span>CPUs</span>
                        <i>16-core CPU</i>
                      </div>
                      <div className={styles.line}>
                        <span>RAM</span>
                        <i>32GB</i>
                      </div>
                      <div className={styles.line}>
                        <span>Storage (SSD)</span>
                        <i>2TB SSD Storage</i>
                      </div>
                      <div className={styles.line}>
                        <span>Network</span>
                        <i>1Gbps for uplink and downlink bandwidth</i>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Download */}
                <div className={styles.card} id="download" ref={downloadRef}>
                  <div className={styles.title}>
                    <img
                      src="https://static-s3.mtt.xyz/chain-website/cb766c132c1f9925562394e139fc149c.png"
                      alt=""
                    />
                    Download
                  </div>
                  <Button
                    type="primary"
                    className={styles.downloadBtn}
                    onClick={() => {
                      if (window !== undefined) {
                        window.open(LINKS.Ubuntu);
                      }
                    }}
                  >
                    <i>
                      <img
                        src="https://static-s3.mtt.xyz/chain-website/9187bd96372314f319373ddbf664a35f.png"
                        alt=""
                      />
                    </i>
                    H22.04 for Ubuntu
                  </Button>
                  <p>Click here to download</p>
                </div>
                {/* info表单 */}
                <div className={styles.card} id="infos" ref={infoRef}>
                  <div className={styles.title}>
                    <img
                      src="https://static-s3.mtt.xyz/chain-website/68edf2b53e4307dbf5f2a19f4603f664.png"
                      alt=""
                    />
                    Information Review
                  </div>
                  {/* Addresses */}
                  <div className={styles.formCard}>
                    <FormItem
                      label="Consensus Address"
                      name="address"
                      validateStatus={!isSuccess ? 'error' : undefined}
                      help={!isSuccess ? <div>Invalid Address</div> : undefined}
                    >
                      <Input placeholder="mttvaloper1mg9m7dsgceuwmd3e3lzBgr300npxd9kgjzp7w0" />
                    </FormItem>
                    <Button
                      disabled={!address}
                      type="primary"
                      className={classNames(
                        styles.submitBtn,
                        !address && styles.disableBtn
                      )}
                      onClick={queryAddress}
                    >
                      {address ? 'Review' : 'Enter Addresse'}
                    </Button>
                  </div>
                  {/* basic info */}
                  <div className={styles.formCard}>
                    <div className={styles.formTitle}>
                      <div className={styles.index}>1</div>
                      Basic Info
                    </div>
                    <FormItem
                      label="Validator Name"
                      name="moniker"
                      rules={[
                        {
                          required: true,
                          message: 'Validator name is required',
                        },
                        {
                          validator: (_, value) => {
                            // 首先检查首字母是否大写
                            if (value && !/^[A-Z]/.test(value[0])) {
                              return Promise.reject(
                                'The first letter of your validator name must be uppercase'
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                        {
                          pattern: /^[A-Za-z0-9]+$/,
                          message: 'Only alphanumeric characters are allowed',
                        },
                        {
                          min: 3,
                          max: 9,
                          message:
                            'Validator name must be between 3 to 9 characters',
                        },
                      ]}
                    >
                      <Input
                        readOnly
                        placeholder="e.g. Example Validator Name"
                      />
                    </FormItem>
                    <FormItem
                      label="Website URL"
                      name="website"
                      rules={[
                        {
                          pattern:
                            /^(https?:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/,
                          message: 'Invalid URL',
                        },
                      ]}
                    >
                      <Input readOnly placeholder="https://example.com" />
                    </FormItem>
                    <FormItem label="Description" name="details">
                      <Input.TextArea readOnly className={styles.textarea} />
                    </FormItem>
                  </div>

                  {/* Commission */}
                  <div className={styles.formCard}>
                    <div className={styles.formTitle}>
                      <div className={styles.index}>3</div>
                      Commission
                    </div>
                    <FormItem
                      label="Rate"
                      name="rate"
                      tooltip={{
                        title: 'The current commission fee',
                        icon: <InfoCircleOutlined />,
                      }}
                    >
                      <InputNumber
                        readOnly
                        placeholder="e.g. 10.00"
                        controls={false}
                        className={styles.fullWidth}
                      />
                    </FormItem>
                    <FormItem
                      label="Max Rate"
                      name="maxRate"
                      tooltip={{
                        title: 'The maximum allowable commission fee',
                        icon: <InfoCircleOutlined />,
                      }}
                    >
                      <InputNumber
                        readOnly
                        className={styles.fullWidth}
                        placeholder="e.g. 10.00"
                        controls={false}
                      />
                    </FormItem>
                    <FormItem
                      label="Max Change Rate"
                      name="maxChangeRate"
                      tooltip={{
                        title:
                          'The maximum daily adjustment limit for the commission fee',
                        icon: <InfoCircleOutlined />,
                      }}
                    >
                      <InputNumber
                        readOnly
                        className={styles.fullWidth}
                        placeholder="e.g. 10.00"
                        controls={false}
                      />
                    </FormItem>
                  </div>
                  {/* Self Delegation */}
                  <div className={styles.formCard}>
                    <div className={styles.formTitle}>
                      <div className={styles.index}>4</div>
                      Self Delegation
                    </div>
                    <FormItem
                      label="Self Delegate Amount"
                      name="tokens"
                      tooltip={{
                        title: (
                          <>
                            Please note that creating a validator will need an
                            extra <b>10,000 MTT</b> to be locked into an
                            address. You won’t be able to retrieve this.
                          </>
                        ),
                        icon: <InfoCircleOutlined />,
                      }}
                    >
                      <InputNumber
                        readOnly
                        placeholder="e.g. 10,000 MTT"
                        controls={false}
                        className={styles.fullWidth}
                      />
                    </FormItem>
                  </div>
                  <div
                    className={styles.warningTips}
                    id="conform"
                    ref={confirmRef}
                  >
                    <InfoCircleOutlined className={styles.icon} />
                    Please review the basic information of your validator node
                    to ensure its accuracy. Modifying the above information
                    requires command-line operations on the node client.
                  </div>
                  {/* {isSuccess ? (
                    <div className={styles.successBtn}>
                      <CheckCircleOutlined className={styles.icon} />
                      Submitted successfully
                    </div>
                  ) : (
                    <Button type="primary" className={styles.submitBtn}>
                      Submit
                    </Button>
                  )} */}
                </div>
              </div>
            </div>
          </div>
        </Form>
      </ConfigProvider>
    </SecondaryLayout>
  );
};

export default Index;
